"use strict";

/* ------------------------------------------------------------------ *
 * Mapper 118: TxSROM (TKSROM / TLSROM)
 * ------------------------------------------------------------------ *
 * https://www.nesdev.org/wiki/INES_Mapper_118
 *
 * The board is a plain MMC3 -- bank select/data at $8000/$8001, PRG RAM
 * protect at $A001, the A12 IRQ at $C000-$E001 -- with one line rewired:
 * CIRAM A10 is driven by CHR A17 instead of the chip's mirroring output.
 * Consequences, straight from the wiki:
 *
 * - the top bit of every bank data value (the M in `MDDD DDDD`) is the
 *   nametable page the corresponding region fetches from;
 * - $A000's mirroring bit still reaches the MMC3 but goes nowhere, so
 *   writes to it are dropped rather than applied -- applying them would
 *   stomp the mapping the CHR registers just chose;
 * - the header's mirroring bit is meaningless too, hence ownsMirroring:
 *   NES.loadROM must not lay it over what loadROM() derives below.
 *
 * Which registers a nametable fetch at $2000-$2FFF sees is the same
 * question as which registers a fetch at $0000-$0FFF sees, because the
 * MMC3 ignores A13 while fetching name tables. In CHR mode 0 the 2 KB
 * registers R0/R1 answer, one M bit per pair of slots (so only
 * single-screen or horizontal arrangements are reachable); in mode 1 the
 * four 1 KB registers R2-R5 answer, one per slot, and any arrangement is
 * reachable. Outside that mode a register's M bit is ignored, per the
 * wiki's note on the bank data register.
 *
 * Games: Armadillo, Goal! Two (US/Europe), NES Play Action Football,
 * Pro Sport Hockey. The unlicensed Alien Syndrome (U) carries a clean
 * iNES header declaring this mapper as well.
 */

function register(Mappers) {
  Mappers[118] = function (nes) {
    this.nes = nes;
  };

  // Everything but the nametable wiring comes from the MMC3: the register
  // map, the bank registers, the A12 IRQ. DEPENDS_ON in the registry makes
  // sure mapper4 is registered before this module evaluates the expression.
  Mappers[118].prototype = new Mappers[4]();

  // CHR A17 drives CIRAM A10 outright; the header bit has no path to it.
  Mappers[118].prototype.ownsMirroring = true;

  // Re-lays the four name table slots from the M bit (bit 7) of whichever
  // bank registers a nametable fetch responds to in the current CHR mode.
  Mappers[118].prototype.updateNametables = function () {
    var s = this.bankSelect;
    var nt = this.nes.ppu.ntable1;

    if (this.chrAddressSelect === 0) {
      // R0/R1 cover $0000-$0FFF as 2 KB banks, and a 2 KB register carries
      // one A17 line: its M bit feeds both slots of its pair, which reaches
      // single-screen or horizontal only.
      nt[0] = (s[0] >> 7) & 1;
      nt[1] = (s[0] >> 7) & 1;
      nt[2] = (s[1] >> 7) & 1;
      nt[3] = (s[1] >> 7) & 1;
    } else {
      // R2-R5 cover $0000-$0FFF as 1 KB banks: one register per slot.
      nt[0] = (s[2] >> 7) & 1;
      nt[1] = (s[3] >> 7) & 1;
      nt[2] = (s[4] >> 7) & 1;
      nt[3] = (s[5] >> 7) & 1;
    }
  };

  // The M bit rides in with the bank value, so every path that re-derives
  // CHR also has to re-derive the name tables: a $8001 write moves a bank
  // (and may move a nametable with it), and flipping the CHR mode in $8000
  // changes which registers the nametable fetches listen to at all.
  Mappers[118].prototype.executeCommand = function () {
    Mappers[4].prototype.executeCommand.apply(this, arguments);
    this.updateNametables();
  };

  Mappers[118].prototype.updateChrBanks = function () {
    Mappers[4].prototype.updateChrBanks.apply(this, arguments);
    this.updateNametables();
  };

  Mappers[118].prototype.write = function (address, value) {
    // Even $A000-$BFFF is the MMC3 mirroring register. Its output is not
    // connected on a TxSROM board ("This bit is bypassed by the
    // configuration described above, so writing here has no effect"), so the
    // write is consumed here. $A001 (odd) is the PRG RAM protect and still
    // works.
    if (address >= 0xa000 && address <= 0xbfff && (address & 1) === 0) {
      return;
    }
    Mappers[4].prototype.write.apply(this, arguments);
  };

  Mappers[118].prototype.loadROM = function () {
    Mappers[4].prototype.loadROM.apply(this, arguments);
    // The header's mirroring is never consulted (ownsMirroring), so the
    // power-on bank registers are what the name tables read until the game
    // writes one: CIRAM A10 has been following CHR A17 since the first
    // fetch, and the power-on values are all zero.
    this.updateNametables();
  };
}

module.exports = { register: register };
